export type MembershipOutcome = {
  record_id: string;
  status: 'ok' | 'error';
  error?: {
    code: string;
    message: string;
  };
};

export function chunkRecordIds(ids: readonly string[], size = 100): string[][] {
  if (ids.length === 0) return [];
  const chunks: string[][] = [];
  for (let offset = 0; offset < ids.length; offset += size) {
    chunks.push(ids.slice(offset, offset + size));
  }
  return chunks;
}

export function reconcileViewSelection(
  originalIds: readonly string[],
  outcomes: readonly MembershipOutcome[],
  operation: 'add' | 'create' | 'remove',
): string[] {
  const failedIds = outcomes
    .filter((outcome) => outcome.status === 'error')
    .map((outcome) => outcome.record_id);

  if (operation === 'remove') {
    return failedIds;
  }

  if (outcomes.length > 0 && outcomes.every((outcome) => outcome.status === 'ok')) {
    return [...originalIds];
  }

  return failedIds;
}

export function selectionMap(ids: readonly string[]): Record<string, boolean> {
  const next: Record<string, boolean> = {};
  for (const id of ids) {
    next[id] = true;
  }
  return next;
}

export function membershipOutcomeMessage(
  outcomes: readonly MembershipOutcome[],
  fallback: string,
): string {
  for (const outcome of outcomes) {
    if (outcome.status === 'error' && outcome.error?.message) {
      return outcome.error.message;
    }
  }
  return fallback;
}

export const DATASET_SELECTION_PLURAL = {
  account: 'accounts',
  contact: 'contacts',
  project: 'projects',
} as const;

/** Public membership batches are capped at 100; preserve outcome order across chunks. */
export const CHUNK_WRITE_FAILED = 'chunk_write_failed';

function chunkWriteFailure(recordId: string, message: string): MembershipOutcome {
  return {
    record_id: recordId,
    status: 'error',
    error: { code: CHUNK_WRITE_FAILED, message },
  };
}

/**
 * Runs membership writes in ≤100 ID chunks and aggregates ordered outcomes.
 * If a later chunk throws after earlier successes, returns collected successes
 * plus failure markers for the failed chunk and any remaining IDs so callers
 * can always reconcile selection.
 */
export async function runChunkedMembership(
  ids: readonly string[],
  write: (chunk: readonly string[]) => Promise<{ results: readonly MembershipOutcome[] }>,
): Promise<MembershipOutcome[]> {
  const outcomes: MembershipOutcome[] = [];
  const chunks = chunkRecordIds(ids);

  for (let index = 0; index < chunks.length; index += 1) {
    const chunk = chunks[index] ?? [];
    try {
      const result = await write(chunk);
      outcomes.push(...result.results);
    } catch (err) {
      const message = err instanceof Error && err.message ? err.message : 'Membership write failed';
      const remaining = chunks.slice(index).flat();
      for (const recordId of remaining) {
        outcomes.push(chunkWriteFailure(recordId, message));
      }
      return outcomes;
    }
  }

  return outcomes;
}
